/**
 * EVA density guide: the reference page the supplier publishes for milled
 * EVA insoles (hardness table, colour swatches, technical data), shown from
 * the EVA panel of the insole design section.
 *
 * Plain module (no React) so a test can check the file exists under
 * apps/web/public without rendering anything: the path is root-relative,
 * exactly as the length illustrations in InsoleDesignSection are served, and
 * a path with no file behind it is a 404 in production, not a build error.
 */

/**
 * The English copy, keyed by its orders-namespace key. The single source for
 * the components' `defaultValue` (so the Leather/Quantity/EvaThickness-style
 * dialog tests, which run react-i18next uninitialised, see real text) AND for
 * the locale test that pins en/orders.json to exactly these strings.
 */
export const EVA_DENSITY_GUIDE_COPY = {
  eva_density_help_link: 'Want more detail on the EVA density types? Click here',
  eva_density_modal_title: 'EVA density types',
  eva_density_modal_description:
    'The reference page for milled EVA insoles: hardness table, colour swatches and technical data.',
  eva_density_modal_hint: 'Tap an image to open it at full size.',
  eva_density_image_2_alt:
    'EVA insoles details: hardness table, colour blocks and technical data',
} as const;

export type EvaDensityGuideKey = keyof typeof EVA_DENSITY_GUIDE_COPY;

/** Pixel size of the page (A4 at 300 dpi). Set on the <img> so the modal
 *  reserves the aspect ratio and does not jump while the page decodes. */
export const EVA_DENSITY_GUIDE_IMAGE_WIDTH = 2480;
export const EVA_DENSITY_GUIDE_IMAGE_HEIGHT = 3508;

/**
 * Exactly one page. The supplier's photo page (page 1, the density
 * variants) was dropped on 2026-09-22: the technical page alone answers
 * "what does this EVA type mean". The array shape stays so the modal keeps
 * one render path and a future page is a one-line addition.
 */
export const EVA_DENSITY_GUIDE_IMAGES: ReadonlyArray<{
  src: string;
  /** orders-namespace key for the alt text; `altDefault` is the English. */
  altKey: EvaDensityGuideKey;
  altDefault: string;
}> = [
  {
    src: '/prescription/eva-density/eva-insoles-details-2.png',
    altKey: 'eva_density_image_2_alt',
    altDefault: EVA_DENSITY_GUIDE_COPY.eva_density_image_2_alt,
  },
];
